import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'baseui/snackbar'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'
import { fidelityMaximizerModuleActions } from './FidelityMaximizerSagas'
import {
  selectConfig,
  selectContactConfig,
  selectFidelityMaximizerState,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { updateFidelityMaximizer } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import useFidelityMaximizerEligibility from './useFidelityMaximizerEligibility'
import useFidelityActionRealtimeSync from './useFidelityActionRealtimeSync'
import useSecondsUntil from '@/shared/hooks/useSecondsUntil'
import { formatFrenchSentAt, formatFrenchTime } from './formatFrenchDateTime'
import {
  CONNECTION_CHOICES,
  FIDELITY_COPY,
  FULLY_LOYAL_SCENARIO,
  HANDOVER_ACTION,
  JOURNEY_COPY,
  JOURNEY_VIEWS,
  MYSELECTRA_NO_APP_SCENARIO,
  PENDING_REGISTRATION_SCENARIO,
  groupCode,
  joinFieldLabels,
  resolveJourneyView,
  resolvePillState,
  resolveStepIndex,
} from './fidelityJourney'
import { clamp } from '@/shared/tools/dateTime'
import { escapeHtml } from '@/shared/tools/stringFunctions'
import { SoftButtonOverrides } from './styled-components'

const REGISTRATION_CODE_ACTION = 'myselectra_registration_code'
const NLBP_ACTIVATION_NOTIFICATION = 'nlbp-account-activation'
const HANDOVER_WELCOME_NOTIFICATION = 'hop-handover-welcome'

const RESEND_COOLDOWN_SECONDS = 30
const REGENERATE_COOLDOWN_SECONDS = 10
const NO_CODE_OUTCOMES = ['nlbp_activation', 'account_exists']
const CHECKLIST_KEYS = ['nlbp_classic', 'nlbp_personalized', 'myselectra', 'app_downloaded']
const STEP_KEYS = ['send', 'connection', 'activated']
const VIEWS_WITHOUT_ACTION = [JOURNEY_VIEWS.MAXIMIZED, JOURNEY_VIEWS.STATUS_ONLY]

const SCENARIO_COPY_KEYS = {
  nothing: 'scenario_1',
  nlbp_classic_only: 'scenario_1',
  nlbp_personalized_only: 'scenario_3',
  myselectra_no_app: 'scenario_4',
}

const MYSELECTRA_COPY_KEYS = ['scenario_1_with_pdl', 'scenario_3']

export const resolveLegacyCopyKey = (scenarioKey, isPdlEligible) => {
  const baseCopyKey = SCENARIO_COPY_KEYS[scenarioKey ?? '']

  if (!baseCopyKey) {
    return null
  }

  if (baseCopyKey !== 'scenario_1') {
    return baseCopyKey
  }

  return isPdlEligible ? 'scenario_1_with_pdl' : 'scenario_1_without_pdl'
}

// The same broadcast carries legacy newsletter actions, whose payload never has a pending_user.
export const resolveActionOutcome = actionResult => {
  if (!actionResult || actionResult.actionKey !== HANDOVER_ACTION) {
    return null
  }

  const { success, message, data } = actionResult

  if (!success) {
    return { kind: 'error', message }
  }

  if (data?.notification === NLBP_ACTIVATION_NOTIFICATION) {
    return { kind: 'nlbp_activation', message }
  }

  return { kind: data?.pending_user ? 'pending' : 'account_exists', message }
}

const resolveNoCodeKind = (outcome, pendingRegistration) => {
  if (outcome && NO_CODE_OUTCOMES.includes(outcome.kind)) {
    return outcome.kind
  }

  return pendingRegistration?.notification === NLBP_ACTIVATION_NOTIFICATION ? 'nlbp_activation' : null
}

const useFidelityMaximizer = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueue } = useSnackbar()
  const { contactId } = useSelector(selectConfig)
  const { contractLoaded } = useSelector(selectContactConfig)
  const fidelityMaximizer = useSelector(selectFidelityMaximizerState)
  const {
    loading,
    loaded,
    enabled,
    checklist,
    isPdlEligible,
    missingContactFields = [],
    scenarioKey,
    actionKey,
    actionAvailable,
    actionKeys = null,
    pendingRegistration = null,
    actionLoading,
    actionResult = null,
    codeLoading = false,
    registrationCode = null,
    connectionChoice = null,
    journeyContactId = null,
  } = fidelityMaximizer

  const isEligibleAgent = useFidelityMaximizerEligibility()

  // action_keys only come with the MySelectra handover; without them the module keeps the newsletter card.
  const journeyEnabled = Array.isArray(actionKeys)
  const primaryActionKey = journeyEnabled ? (actionKeys[0] ?? null) : actionKey
  const isPendingRegistration = scenarioKey === PENDING_REGISTRATION_SCENARIO
  const resendActionKey = journeyEnabled && isPendingRegistration ? (actionKeys[1] ?? null) : null

  const contactCode = registrationCode?.contactId === contactId ? registrationCode : null
  const choice = connectionChoice?.contactId === contactId ? connectionChoice.value : null
  // MySelectra's own email (resend, client already on the newsletter) has no sign-up link: the code is the only way.
  const codeOnly = isPendingRegistration && pendingRegistration?.notification === HANDOVER_WELCOME_NOTIFICATION
  const connection = codeOnly ? CONNECTION_CHOICES.APP : choice
  const outcome = actionResult?.contactId === contactId ? resolveActionOutcome(actionResult) : null
  const noCodeKind = resolveNoCodeKind(outcome, pendingRegistration)

  const view = resolveJourneyView({
    journeyEnabled,
    scenarioKey,
    primaryActionKey,
    actionAvailable,
    noCodeExpected: noCodeKind !== null,
    journeyStartedForContact: journeyContactId === contactId,
    myselectraAccount: checklist?.myselectra === true,
  })

  // The action broadcast clears actionLoading, but the view only moves once the status reload lands.
  // Without this the agent can send the handover email a second time in between.
  const handoverAwaitingStatus = view === JOURNEY_VIEWS.SEND && outcome?.kind === 'pending'

  const handedOverTime = pendingRegistration?.handed_over_at
    ? new Date(pendingRegistration.handed_over_at).getTime()
    : NaN

  // handed_over_at comes from the server clock, so anchor the deadline locally once and cap it at the
  // cooldown: a workstation running behind the server must not be able to stretch the wait.
  const resendDeadline = useMemo(() => {
    if (Number.isNaN(handedOverTime)) {
      return null
    }

    const window = RESEND_COOLDOWN_SECONDS * 1000
    return new Date(Date.now() + clamp(handedOverTime + window - Date.now(), 0, window)).toISOString()
  }, [handedOverTime])

  const resendSeconds = useSecondsUntil(resendDeadline)
  const regenerateSeconds = useSecondsUntil(contactCode?.issuedAt, REGENERATE_COOLDOWN_SECONDS)
  const codeSeconds = useSecondsUntil(contactCode?.expiresAt)
  const contactIncomplete = missingContactFields.length > 0

  const inFlightAction = useRef(null)

  // A regenerated code can repeat, so the progress window is measured from its expiry rather than from the code.
  const codeValidity = useRef({ expiresAt: undefined, total: 0 })

  if (codeValidity.current.expiresAt !== contactCode?.expiresAt) {
    codeValidity.current = { expiresAt: contactCode?.expiresAt, total: Math.max(codeSeconds, 1) }
  }

  const handleActionCompleted = useCallback(
    ({ success, message, data }) => {
      const actionResult = { contactId, actionKey: inFlightAction.current, success, message, data }
      dispatch(updateFidelityMaximizer({ actionLoading: false, actionResult }))
      showSnackbar(enqueue, {
        type: success ? SNACKBAR_TYPES.SUCCESS : SNACKBAR_TYPES.ERROR,
        message: message || (success ? 'Action envoyée avec succès.' : "Erreur lors de l'envoi."),
      })
    },
    [dispatch, enqueue, contactId],
  )

  useFidelityActionRealtimeSync(contactId, isEligibleAgent, handleActionCompleted)

  useEffect(() => {
    if (!contractLoaded || !contactId || !isEligibleAgent || loaded || loading) {
      return
    }

    dispatch({ type: fidelityMaximizerModuleActions.FIDELITY_MAXIMIZER_LOAD_STATUS, contactId })
  }, [contractLoaded, contactId, isEligibleAgent, loaded, loading])

  const dispatchAction = action => {
    inFlightAction.current = action
    dispatch({
      type: fidelityMaximizerModuleActions.FIDELITY_MAXIMIZER_TRIGGER_ACTION,
      contactId,
      action,
      enqueue,
    })
  }

  const moduleBlocked = !actionAvailable || contactIncomplete
  const actionBlocked = moduleBlocked || actionLoading
  const codeRequestBlocked = moduleBlocked || codeLoading

  const triggerAction = () => {
    const isSendable = primaryActionKey && primaryActionKey !== REGISTRATION_CODE_ACTION
    const cannotSend = !isSendable || actionBlocked || handoverAwaitingStatus

    if (cannotSend) {
      return
    }

    dispatchAction(primaryActionKey)
  }

  const resendHandover = () => {
    const cannotResend = !resendActionKey || actionBlocked || resendSeconds > 0

    if (cannotResend) {
      return
    }

    dispatchAction(resendActionKey)
  }

  const requestRegistrationCode = () => {
    const cannotRequestCode = !isPendingRegistration || codeRequestBlocked || regenerateSeconds > 0

    if (cannotRequestCode) {
      return
    }

    dispatch({
      type: fidelityMaximizerModuleActions.FIDELITY_MAXIMIZER_ISSUE_REGISTRATION_CODE,
      contactId,
      enqueue,
    })
  }

  const chooseConnection = value => () => dispatch(updateFidelityMaximizer({ connectionChoice: { contactId, value } }))

  useEffect(() => {
    if (codeOnly && choice !== CONNECTION_CHOICES.APP) chooseConnection(CONNECTION_CHOICES.APP)()
  }, [codeOnly, choice])

  const isLegacy = view === JOURNEY_VIEWS.LEGACY
  const isConnection = view === JOURNEY_VIEWS.CONNECTION
  const sentBoxVisible = isConnection || view === JOURNEY_VIEWS.NO_CODE
  const hasCode = Boolean(contactCode?.code)
  const codeValid = hasCode && codeSeconds > 0

  const contactWarningText = t(`${FIDELITY_COPY}.contact_incomplete_warning`, {
    fields: joinFieldLabels(
      missingContactFields.map(field => t(`${FIDELITY_COPY}.contact_fields.${field}`, { defaultValue: field })),
    ),
  })

  const remainingLabel =
    codeSeconds >= 60
      ? t(`${JOURNEY_COPY}.dictation.remaining_minutes`, {
          minutes: Math.floor(codeSeconds / 60),
          seconds: codeSeconds % 60,
        })
      : t(`${JOURNEY_COPY}.dictation.remaining_seconds`, { seconds: codeSeconds })

  const stepIndex = resolveStepIndex(view)

  const legacyCopyKey = resolveLegacyCopyKey(scenarioKey, isPdlEligible)
  const isFullyLoyal = scenarioKey === FULLY_LOYAL_SCENARIO
  const legacyCardHidden = !actionAvailable || !actionKey || !legacyCopyKey
  const pdlOkBannerKey = MYSELECTRA_COPY_KEYS.includes(legacyCopyKey ?? '')
    ? 'pdl_ok_banner_propose_myselectra'
    : 'pdl_ok_banner'
  const sentBoxBadge = pendingRegistration?.newsletter
    ? t(`${JOURNEY_COPY}.newsletter_labels.${pendingRegistration.newsletter}`, { defaultValue: '' })
    : ''

  return {
    hidden: !isEligibleAgent || !loaded || !enabled,
    view,
    missingContactFields,
    registrationCode: hasCode ? { code: contactCode.code, expiresAt: contactCode.expiresAt ?? null } : null,
    outcome,
    resendSeconds,
    regenerateSeconds,
    triggerAction,
    resendHandover,
    requestRegistrationCode,
    legacy: {
      hidden: !isLegacy,
      loyaltyBannerText: `🏆 ${t(`${FIDELITY_COPY}.maximized_label`)}`,
      loyaltyBannerHidden: !isFullyLoyal,
      pdlWarningText: t(`${FIDELITY_COPY}.pdl_missing_warning`),
      pdlWarningHidden: isFullyLoyal || isPdlEligible,
      pdlOkBannerText: t(`${FIDELITY_COPY}.${pdlOkBannerKey}`),
      pdlOkBannerHidden: isFullyLoyal || !isPdlEligible,
      card: {
        hidden: legacyCardHidden,
        copyKey: legacyCopyKey ?? '',
        disabled: contactIncomplete,
        loading: actionLoading,
        onClick: triggerAction,
      },
      contactWarning: { hidden: !contactIncomplete || legacyCardHidden, text: contactWarningText },
    },
    journey: {
      hidden: isLegacy,
      pills: CHECKLIST_KEYS.map(key => ({
        key,
        label: t(`${FIDELITY_COPY}.checklist.${key}`),
        state: resolvePillState(key, checklist?.[key] === true, isPendingRegistration),
      })),
      stepsHidden: stepIndex === null,
      steps: STEP_KEYS.map((key, index) => ({
        key,
        label: t(`${JOURNEY_COPY}.steps.${key}`),
        done: stepIndex !== null && index < stepIndex,
        current: index === stepIndex,
      })),
      pdlWarning: {
        hidden: view !== JOURNEY_VIEWS.SEND || isPdlEligible,
        text: t(`${FIDELITY_COPY}.pdl_missing_warning`),
      },
      contactWarning: {
        hidden: !contactIncomplete || VIEWS_WITHOUT_ACTION.includes(view),
        text: contactWarningText,
      },
      send: {
        hidden: view !== JOURNEY_VIEWS.SEND,
        script: t(`${JOURNEY_COPY}.send.${checklist?.nlbp_classic ? 'script_already_subscribed' : 'script'}`),
        info: t(`${JOURNEY_COPY}.send.info`),
        action: {
          label: t(`${JOURNEY_COPY}.send.cta`),
          disabled: contactIncomplete || handoverAwaitingStatus,
          loading: actionLoading || loading,
          onClick: triggerAction,
        },
      },
      appLinks: {
        hidden: view !== JOURNEY_VIEWS.APP_LINKS,
        script: t(`${JOURNEY_COPY}.app_links.script`),
        info: t(`${JOURNEY_COPY}.app_links.info`),
        action: {
          label: t(`${JOURNEY_COPY}.app_links.cta`),
          disabled: contactIncomplete,
          loading: actionLoading,
          onClick: triggerAction,
        },
      },
      sentBox: {
        hidden: !sentBoxVisible || !pendingRegistration,
        title: t(`${JOURNEY_COPY}.sent_box.title`),
        sentAt: t(`${JOURNEY_COPY}.sent_box.sent_at`, {
          time: formatFrenchSentAt(pendingRegistration?.handed_over_at),
        }),
        badge: sentBoxBadge,
        badgeHidden: !sentBoxBadge,
      },
      noCode: {
        hidden: view !== JOURNEY_VIEWS.NO_CODE,
        text: noCodeKind ? t(`${JOURNEY_COPY}.no_code.${noCodeKind}`) : '',
      },
      choice: {
        hidden: !isConnection || codeOnly,
        app: { selected: choice === CONNECTION_CHOICES.APP, onSelect: chooseConnection(CONNECTION_CHOICES.APP) },
        email: {
          selected: choice === CONNECTION_CHOICES.EMAIL,
          onSelect: chooseConnection(CONNECTION_CHOICES.EMAIL),
        },
      },
      codeOnlyNotice: {
        hidden: !isConnection || !codeOnly,
        text: t(`${JOURNEY_COPY}.connection.code_only`),
      },
      app: {
        hidden: !isConnection || connection !== CONNECTION_CHOICES.APP,
        introHidden: hasCode,
        subjectHidden: codeOnly,
        dictationHidden: !codeValid,
        dictationScript: t(`${JOURNEY_COPY}.dictation.script`, { code: escapeHtml(contactCode?.code ?? '') }),
        info: t(hasCode ? `${JOURNEY_COPY}.dictation.info` : `${JOURNEY_COPY}.app.info`),
        code: {
          hidden: !hasCode,
          code: groupCode(contactCode?.code ?? ''),
          expired: hasCode && !codeValid,
          expiryLabel: codeValid
            ? t(`${JOURNEY_COPY}.dictation.expires_at`, {
                time: formatFrenchTime(contactCode?.expiresAt),
                remaining: remainingLabel,
              })
            : t(`${JOURNEY_COPY}.dictation.expired`),
          progress: { value: codeSeconds, maxValue: codeValidity.current.total },
        },
        generateAction: {
          hidden: codeValid,
          label: t(hasCode ? `${JOURNEY_COPY}.dictation.cta` : `${JOURNEY_COPY}.app.cta`),
          refreshIcon: hasCode,
          disabled: contactIncomplete || regenerateSeconds > 0,
          loading: codeLoading,
          onClick: requestRegistrationCode,
        },
        regenerateAction: {
          hidden: !codeValid,
          label:
            regenerateSeconds > 0
              ? t(`${JOURNEY_COPY}.dictation.cta_countdown`, { seconds: regenerateSeconds })
              : t(`${JOURNEY_COPY}.dictation.cta`),
          disabled: regenerateSeconds > 0,
          loading: codeLoading,
          onClick: requestRegistrationCode,
        },
      },
      email: {
        hidden: !isConnection || connection !== CONNECTION_CHOICES.EMAIL,
      },
      activated: {
        hidden: view !== JOURNEY_VIEWS.ACTIVATED,
        text: t(`${JOURNEY_COPY}.activated.${isFullyLoyal || checklist?.app_downloaded === true ? 'app' : 'email'}`),
        appLinksAction: {
          hidden: scenarioKey !== MYSELECTRA_NO_APP_SCENARIO || choice === CONNECTION_CHOICES.APP,
          label: t(`${JOURNEY_COPY}.activated.send_app_links`),
          disabled: contactIncomplete,
          loading: actionLoading,
          onClick: triggerAction,
        },
        appLinksOverrides: SoftButtonOverrides,
      },
      maximized: {
        hidden: view !== JOURNEY_VIEWS.MAXIMIZED,
        text: t(`${JOURNEY_COPY}.maximized`),
      },
      error: {
        hidden: outcome?.kind !== 'error',
        text: t(`${JOURNEY_COPY}.send_error`, { message: escapeHtml(outcome?.message ?? '') }),
      },
      resend: {
        hidden: !resendActionKey || !sentBoxVisible || (hasCode && connection === CONNECTION_CHOICES.APP),
        label:
          resendSeconds > 0
            ? t(`${JOURNEY_COPY}.connection.resend_countdown`, { seconds: resendSeconds })
            : t(`${JOURNEY_COPY}.connection.resend`),
        disabled: resendSeconds > 0,
        loading: actionLoading,
        onClick: resendHandover,
      },
    },
  }
}

export default useFidelityMaximizer
